import BigNumber from 'bignumber.js'
import dayjs from 'dayjs'
import { BN } from '@coral-xyz/anchor'
import {
  getAssociatedTokenAddress,
  getMint,
  NATIVE_MINT
} from '@solana/spl-token'
import {
  BlockhashWithExpiryBlockHeight,
  // ComputeBudgetProgram,
  // ConfirmOptions,
  LAMPORTS_PER_SOL,
  PublicKey,
  // Signer,
  Transaction,
  // Transaction,
  // TransactionInstruction,
  // TransactionMessage,
  VersionedTransaction
} from '@solana/web3.js'
import {
  BetSDK,
  BetState,
  // DEFAULT_COMMITMENT,
  DEFAULT_DECIMALS
} from '@terminusbet/bet-sdk'
import { StakeVoteSDK } from '@terminusbet/stake-vote-sdk'

import ENV_PARAMS from '@/constants/envParams'
import {
  // DEFAULT_COMPUTE_UNIT_LIMIT,
  DEFAULT_TRANSACTION_SETTING,
  FeeMode,
  ITxSettingProps,
  PriorityFeeLevel,
  // SUPPORT_RPC_ENDPOINT,
  TOKEN_MARKET_CAP_SOL
} from '@/constants/settings'
import { Decimal } from '@/constants/trade'
import {
  fetchHeliusPriorityFees,
  fetchQuiknodePriorityFees
} from '@/lib/apiFetcher'
import { IMetadata, MigrateDex } from '@/lib/solana/types'

import { solanaProvider } from './solana/lib/providerFactory'
import { TradeProvider } from './solana/lib/tradeProvider'
import { getTokenMetadata, packageTransaction } from './solana/utils'
import { min2Max } from './utils'

// const CONFIRM_OPTIONS_TRADE: ConfirmOptions = {
//   // skipPreflight: true,
//   preflightCommitment: 'processed',
//   commitment: 'confirmed'
// }
// const CONFIRM_OPTIONS_TRADE: ConfirmOptions = { commitment: 'finalized' }

export { DEFAULT_DECIMALS, type IMetadata, MigrateDex, NATIVE_MINT }

export enum EnumSwapBase {
  IN = 'BaseIn',
  OUT = 'BaseOut'
}

export enum StakeAddType {
  Time = 0,
  Token = 1
}

export enum StakeStauts {
  NoStaked = 'NoStaked',
  Staked = 'Staked',
  Expired = 'Expired'
}

export interface ITradeProps {
  mint: string
  amount: string
  slippageBps: number
}

export interface ITradeTransaction {
  tx: VersionedTransaction
  latestBlockhash?: BlockhashWithExpiryBlockHeight
}

export interface IComputeProps {
  base: 'BaseIn' | 'BaseOut'
  inputMint: string
  outputMint: string
  amount: string
  slippageBps?: number
  feeRate?: number
}

export interface IComputeData {
  swapType: 'BaseIn' | 'BaseOut'
  inputMint: string
  inputAmount: string
  outputMint: string
  outputAmount: string
  payAmount: string
  otherAmountThreshold: string
  slippageBps: number
  priceImpactPct: number
  // routePlan: {
  //   poolId: string,
  //   inputMint: string,
  //   outputMint: string,
  //   feeMint: string,
  //   feeRate: number,
  //   feeAmount: string,
  // }[]
}

export interface StakePool {
  publicKey: PublicKey
  decimal: number
  stakePeriod: number
  rewardGrowBy: number
  rewardGrowMinBy: number
  stakePeriodByMinutes: number
  stakePeriodByHours: number
  stakePeriodByDays: number
  stakePeriodByMonths: number
}
export interface UserStakedPool {
  publicKey: PublicKey
  timeEndAt: number
  timeStartAt: number
  periodTotal: number
  stakeTotal: number
  stakeReleaseNotRecived: string
  stakeNotRelease: string
  voteUsed: number
  voteTotal: number
  voteAvailable: number
  voteLocked: number
}

export default class BetSdkUtils {
  readonly betSdkInstance: BetSDK | undefined
  readonly stakeVoteSdk: StakeVoteSDK
  private tradeProviderInstance: TradeProvider | undefined
  private tradeSetting: ITxSettingProps

  constructor(options?: {
    provider?: TradeProvider
    txSetting?: ITxSettingProps
  }) {
    if (options?.provider) {
      this.tradeProviderInstance = options.provider
    }
    this.tradeSetting = options?.txSetting || DEFAULT_TRANSACTION_SETTING
    try {
      this.betSdkInstance = new BetSDK(
        solanaProvider.provider,
        import.meta.env.VITE_BET_SDK_MODE === 'development'
      )
      console.log('>>>>>> this.betSdkInstance: ', this.betSdkInstance)
    } catch (e) {
      console.error(e)
    }
    this.stakeVoteSdk = new StakeVoteSDK(solanaProvider.provider)
  }

  // 获取betSdk实例
  public get betSdk(): BetSDK | undefined {
    return this.betSdkInstance
  }

  // 获取tradeProvider实例
  public get tradeProvider(): TradeProvider | undefined {
    return this.tradeProviderInstance
  }

  public get txSetting(): ITxSettingProps {
    return this.tradeSetting
  }

  public setProvider(provider: TradeProvider | undefined) {
    if (this.tradeProviderInstance !== provider) {
      this.tradeProviderInstance = provider
    }
  }

  public setTxSetting(txSetting: ITxSettingProps) {
    if (this.tradeSetting !== txSetting) {
      this.tradeSetting = txSetting
    }
  }

  public getBetSdk(): BetSDK {
    if (!this.betSdkInstance) {
      throw new Error('sdk is undefined')
    }
    return this.betSdkInstance
  }

  public getTradeProvider() {
    if (!this.tradeProviderInstance) {
      throw new Error('Unknow wallet provider')
    }
    return this.tradeProviderInstance
  }

  /**
   *  获取token余额
   * @param mint token mint address
   * @returns
   */
  public async getTokenBalance(mint: string): Promise<{
    amount: number
    decimals: number
  }> {
    let amount, decimals
    const provider = this.getTradeProvider()
    const userPublicKey = provider.publicKey!
    if (mint === NATIVE_MINT.toBase58()) {
      const accountInfo =
        await provider.connection.getAccountInfo(userPublicKey)
      if (!accountInfo) {
        return Promise.reject('Account not found')
      }
      amount = accountInfo.lamports
      decimals = 9
    } else {
      const tokenAccount = await getAssociatedTokenAddress(
        new PublicKey(mint),
        userPublicKey
      )
      const accountInfo = await provider.connection.getAccountInfo(tokenAccount)
      if (!accountInfo) {
        return Promise.reject('Account not found')
      }
      const tokenBalance =
        await provider.connection.getTokenAccountBalance(tokenAccount)
      amount = Number(tokenBalance.value.amount)
      decimals = tokenBalance.value.decimals
    }
    return {
      amount,
      decimals
    }
  }

  public async getStateAccount(mint: string): Promise<BetState | null> {
    const betSdk = this.getBetSdk()
    const stateAccount = await betSdk.getBetStateAccount(new PublicKey(mint))
    return stateAccount
  }

  /**
   * 计算实时优先费
   * @param setting 交易配置
   * @param account 用户钱包地址
   * @returns
   */
  public async calcPriorityFee(
    setting: ITxSettingProps,
    account?: string
  ): Promise<number> {
    // const feeCalculator = await this.tradeProvider.connection.getRecentPrioritizationFees();
    // console.log('feeCalculator', feeCalculator)

    let priorityFee = 0
    // 设置的优先费值
    let feeValueWei = new BigNumber(setting.feeValue)
      .multipliedBy(LAMPORTS_PER_SOL)
      .dp(0, BigNumber.ROUND_FLOOR)
      .toNumber()
    feeValueWei = feeValueWei >= 0 ? feeValueWei : 0
    if (setting.feeMode === FeeMode.ExactFee) {
      // 固定优先费模式
      priorityFee = feeValueWei
    } else if (setting.feeMode === FeeMode.MaxCap) {
      // 动态优先费模式
      let respPriorityFees: any
      if (!respPriorityFees) {
        // 请求Quiknode优先费
        await fetchQuiknodePriorityFees(account)
          .then((resp) => {
            const level: any = resp?.per_compute_unit || {}
            respPriorityFees = {
              low: level.low,
              medium: level.medium,
              high: level.high,
              veryHigh: level.extreme
            }
          })
          .catch(() => undefined)
      }
      if (!respPriorityFees) {
        // 请求Helius优先费
        await fetchHeliusPriorityFees(account)
          .then((resp) => {
            const level: any = resp?.priorityFeeLevels || {}
            respPriorityFees = {
              low: level.low,
              medium: level.medium,
              high: level.high,
              veryHigh: level.veryHigh
            }
          })
          .catch(() => undefined)
      }
      if (respPriorityFees) {
        // 根据优先费等级取优先费
        priorityFee =
          setting.feeLevel === PriorityFeeLevel.Fast
            ? respPriorityFees.medium
            : setting.feeLevel === PriorityFeeLevel.Turbo
              ? respPriorityFees.high
              : setting.feeLevel === PriorityFeeLevel.Ultra
                ? respPriorityFees.veryHigh
                : respPriorityFees.low
      }
      priorityFee = Math.min(priorityFee, feeValueWei)
    }
    return priorityFee
  }

  /**
   * 获取兑换数量数据
   * @param params
   * @returns
   */
  public async getComputeData(params: IComputeProps): Promise<IComputeData> {
    const slippageBps = Number(this.tradeSetting.slippage)
    const baseDir = params.base
    let amount = params.amount
    // const feeRate = params.feeRate || 0;
    // if (baseDir === EnumSwapBase.IN && feeRate) {
    //   // 兑换数量先减掉平台手续费
    //   amount = new BigNumber(params.amount).multipliedBy(BigNumber(1).minus(feeRate)).toFixed();
    // }
    // 忽略小数位
    amount = new BigNumber(params.amount).dp(0, BigNumber.ROUND_FLOOR).toFixed()
    if (Number(amount) <= 0) {
      return Promise.reject()
    }
    const dic = await this.calcComputeData({
      base: params.base,
      inputMint: params.inputMint,
      outputMint: params.outputMint,
      slippageBps: slippageBps,
      amount: amount,
      feeRate: params.feeRate
    }).then((resp: any) => {
      if (resp.data.success) {
        return resp.data
      } else {
        return Promise.reject(resp.data.msg)
      }
    })
    const computeData = dic.data
    if (baseDir === EnumSwapBase.IN) {
      // 设置支付数量
      computeData.payAmount = params.amount
    } else if (baseDir === EnumSwapBase.OUT) {
      // if (feeRate) {
      //   // 支付数量加上平台手续费
      //   computeData.inputAmount = new BigNumber(computeData.inputAmount).multipliedBy(BigNumber(1).plus(feeRate)).dp(0, BigNumber.ROUND_CEIL).toFixed();
      //   computeData.otherAmountThreshold = new BigNumber(computeData.otherAmountThreshold).multipliedBy(BigNumber(1).plus(feeRate)).dp(0, BigNumber.ROUND_CEIL).toFixed();
      // }
      computeData.payAmount = computeData.inputAmount
    }
    return computeData
  }

  // public async getTokenAmount(mint: string, amountSol: string) {
  //   const mintPublicKey = new PublicKey(mint);
  //   const betState = await this.betSdk.getBetStateAccount(mintPublicKey, DEFAULT_COMMITMENT);
  //   if (!betState) {
  //     throw new Error(`Bet bet account not found: ${mint}`);
  //   }
  //   const curveAccount = await this.betSdk.getCurveStateAccount(mintPublicKey, DEFAULT_COMMITMENT);
  //   if (!curveAccount) {
  //     throw new Error(`Curve state account not found: ${mint}`);
  //   }
  //   const solAmount = BigNumber(amountSol).dp(0, BigNumber.ROUND_FLOOR).toFixed();
  //   const tokenAmount = curveAccount.getBuyPrice(BigInt(solAmount), betState.isCompleted);
  //   return {
  //     solAmount,
  //     tokenAmount,
  //   }
  // }

  /**
   * 获取联合曲线进度
   * @param mint token address
   * @returns
   */
  public async getCurveStateData(mint: string): Promise<{
    // progressSol: number,
    progressToken: number
    realSolReserves: string
    realTokenReserves: string
    marketCapSol: string
    completeSol: string
    supply: string
    isCompleted: boolean
    creator: string
    curve: string
    poolOfSol: string
    poolOfToken: string
  }> {
    if (!mint) {
      throw new Error('Unkonw token mint')
    }
    const betSdk = this.getBetSdk()
    const mintPublicKey = new PublicKey(mint)
    const curveStateAccount = await betSdk.getCurveStateAccount(mintPublicKey)
    const betStateAccount = await betSdk.getBetStateAccount(mintPublicKey)
    // console.log('mintPublicKey', mintPublicKey.toBase58())
    // console.log('curveStateAccount', curveStateAccount)
    // console.log('betStateAccount', betStateAccount)

    if (!betStateAccount || !curveStateAccount) {
      throw new Error('Unkonw token mint')
    }
    // const ata = await getAssociatedTokenAddress(mintPublicKey, betStateAccount.poolOfToken);
    const balance = await solanaProvider.connection.getTokenAccountBalance(
      betStateAccount.poolOfToken,
      'processed'
    )
    // console.log('betStateAccount', betStateAccount, curveStateAccount)
    const poolOfTokenBalance = balance.value.uiAmount || 0
    const tokenBalance = Number(poolOfTokenBalance.toFixed(0))
    // let progressSol = 0
    let progressToken = 0
    if (betStateAccount.isCompleted) {
      // progressSol = 100
      progressToken = 100
    } else {
      const initialRealTokenReserves = new BigNumber(
        String(curveStateAccount.supply)
      )
        .dividedBy(Math.pow(10, DEFAULT_DECIMALS))
        .minus(ENV_PARAMS.CURVE_RESERVED_TOKENS)
        .toFixed()
      progressToken = new BigNumber(100)
        .minus(
          BigNumber(tokenBalance)
            .minus(ENV_PARAMS.CURVE_RESERVED_TOKENS)
            .multipliedBy(100)
            .dividedBy(initialRealTokenReserves)
        )
        .toNumber()
      // progressSol = BigNumber(String(curveStateAccount.realSolReserves))
      //   .dividedBy(TOKEN_MARKET_CAP_SOL)
      //   .multipliedBy(100)
      //   .toNumber()
    }

    const marketCapSol =
      (curveStateAccount.supply * curveStateAccount.virtualSolReserves) /
      curveStateAccount.virtualTokenReserves

    return {
      progressToken: Math.max(0, Math.min(progressToken, 100)),
      // progressSol: Math.max(0, Math.min(progressSol, 100)),
      completeSol: String(TOKEN_MARKET_CAP_SOL),
      marketCapSol: String(marketCapSol),
      realSolReserves: String(curveStateAccount.realSolReserves),
      realTokenReserves: String(curveStateAccount.realTokenReserves),
      supply: String(curveStateAccount.supply),
      isCompleted: betStateAccount.isCompleted,
      creator: betStateAccount.creator.toBase58(),
      curve: betStateAccount.curveState.toBase58(),
      poolOfSol: betStateAccount.poolOfSol.toBase58(),
      poolOfToken: betStateAccount.poolOfToken.toBase58()
    }
  }

  public getInitialTokenAmount(amountSol: string) {
    const betSdk = this.getBetSdk()
    const solAmount = BigNumber(amountSol).dp(BigNumber.ROUND_FLOOR).toFixed()
    return betSdk.getInitialBuyPrice(BigInt(solAmount))
  }

  /**
   * 创建并买入token
   * @param metadata
   * @param amountSol
   * @returns
   */
  public async createAndBuyToken(
    metadata: IMetadata,
    amountSol: string
  ): Promise<ITradeTransaction & { mint: string }> {
    const betSdk = this.getBetSdk()
    const tradeProvider = this.getTradeProvider()
    const walletPublicKey = tradeProvider.publicKey!
    const solAmountBN = BigInt(
      BigNumber(amountSol).dp(0, BigNumber.ROUND_FLOOR).toFixed()
    )
    const slippageBN = BigInt(0)
    const priorityFee = await this.calcPriorityFee(
      this.tradeSetting,
      walletPublicKey.toBase58()
    )
    const createResp = await betSdk.getCreateAndBuyTransaction(
      walletPublicKey,
      metadata,
      solAmountBN,
      slippageBN
    )

    const respTx = createResp.newTx
    const respSigners = createResp.mintKeyPair
      ? [createResp.mintKeyPair]
      : undefined

    const tradeTx = await packageTransaction(
      tradeProvider,
      respTx.instructions,
      respSigners,
      {
        priorityFee: priorityFee,
        tableAccounts: [ENV_PARAMS.PLATFORM_LOOKUP_TABLE]
      }
    )

    return {
      tx: tradeTx.transaction,
      latestBlockhash: tradeTx.latestBlockhash,
      mint: createResp.mintKeyPair?.publicKey.toBase58()
    }
  }

  /**
   * 买入token, 固定sol数量
   * @param mint
   * @param amountSol
   * @returns
   */
  public async buyTokenBySolAmount(
    mint: string,
    amountSol: string
  ): Promise<ITradeTransaction> {
    const betSdk = this.getBetSdk()
    const tradeProvider = this.getTradeProvider()
    const slippage = this.tradeSetting.slippage
    const walletPublicKey = tradeProvider.publicKey!
    const mintPublicKey = new PublicKey(mint)
    const solAmountBN = BigInt(
      BigNumber(amountSol).dp(0, BigNumber.ROUND_FLOOR).toFixed()
    )
    const slippageBN = BigInt(
      BigNumber(slippage)
        .multipliedBy(100)
        .dp(0, BigNumber.ROUND_FLOOR)
        .toFixed()
    )
    const priorityFee = await this.calcPriorityFee(
      this.tradeSetting,
      walletPublicKey.toBase58()
    )
    const respTx = await betSdk.getBuyBySolAmountTransaction(
      walletPublicKey,
      mintPublicKey,
      solAmountBN,
      slippageBN
    )
    const tradeTx = await packageTransaction(
      tradeProvider,
      respTx.instructions,
      undefined,
      {
        priorityFee: priorityFee,
        tableAccounts: [ENV_PARAMS.PLATFORM_LOOKUP_TABLE]
      }
    )
    // const signedTx = await this.tradeProvider.wallet.signTransaction(
    //   tradeTx.transaction
    // )

    return {
      tx: tradeTx.transaction,
      latestBlockhash: tradeTx.latestBlockhash
    }
  }

  /**
   * 买入token, 固定token数量
   * @param mint
   * @param amountToken
   * @returns
   */
  public async buyTokenByTokenAmount(
    mint: string,
    amountToken: string
  ): Promise<ITradeTransaction> {
    const betSdk = this.getBetSdk()
    const tradeProvider = this.getTradeProvider()
    const slippage = this.tradeSetting.slippage
    const walletPublicKey = tradeProvider.publicKey!
    const mintPublicKey = new PublicKey(mint)
    const tokenAmountBN = BigInt(
      BigNumber(amountToken).dp(0, BigNumber.ROUND_FLOOR).toFixed()
    )
    const slippageBN = BigInt(
      BigNumber(slippage)
        .multipliedBy(100)
        .dp(0, BigNumber.ROUND_FLOOR)
        .toFixed()
    )
    const priorityFee = await this.calcPriorityFee(
      this.tradeSetting,
      walletPublicKey.toBase58()
    )
    const respTx = await betSdk.getBuyByTokenAmountTransaction(
      walletPublicKey,
      mintPublicKey,
      tokenAmountBN,
      slippageBN
    )
    const tradeTx = await packageTransaction(
      tradeProvider,
      respTx.instructions,
      undefined,
      {
        priorityFee: priorityFee,
        tableAccounts: [ENV_PARAMS.PLATFORM_LOOKUP_TABLE]
      }
    )

    return {
      tx: tradeTx.transaction,
      latestBlockhash: tradeTx.latestBlockhash
    }
  }

  /**
   * 卖出token，固定token数量
   * @param mint
   * @param amountToken
   * @returns
   */
  public async sellTokenByTokenAmount(
    mint: string,
    amountToken: string
  ): Promise<ITradeTransaction> {
    const betSdk = this.getBetSdk()
    const tradeProvider = this.getTradeProvider()
    const slippage = this.tradeSetting.slippage
    const walletPublicKey = tradeProvider.publicKey!
    const mintPublicKey = new PublicKey(mint)
    const tokenAmountBN = BigInt(
      BigNumber(amountToken).dp(0, BigNumber.ROUND_FLOOR).toFixed()
    )
    const slippageBN = BigInt(
      BigNumber(slippage)
        .multipliedBy(100)
        .dp(0, BigNumber.ROUND_FLOOR)
        .toFixed()
    )
    const priorityFee = await this.calcPriorityFee(
      this.tradeSetting,
      walletPublicKey.toBase58()
    )
    const respTx = await betSdk.getSellByTokenAmountTransaction(
      walletPublicKey,
      mintPublicKey,
      tokenAmountBN,
      slippageBN
    )
    const tradeTx = await packageTransaction(
      tradeProvider,
      respTx.instructions,
      undefined,
      {
        priorityFee: priorityFee,
        tableAccounts: [ENV_PARAMS.PLATFORM_LOOKUP_TABLE]
      }
    )

    return {
      tx: tradeTx.transaction,
      latestBlockhash: tradeTx.latestBlockhash
    }
  }

  /**
   * 卖出token，固定sol数量
   * @param mint
   * @param amountToken
   * @returns
   */
  public async sellTokenBySolAmount(
    mint: string,
    amountSol: string
  ): Promise<ITradeTransaction> {
    const betSdk = this.getBetSdk()
    const tradeProvider = this.getTradeProvider()
    const slippage = this.tradeSetting.slippage
    const walletPublicKey = tradeProvider.publicKey!
    const mintPublicKey = new PublicKey(mint)
    const solAmountBN = BigInt(
      BigNumber(amountSol).dp(0, BigNumber.ROUND_FLOOR).toFixed()
    )
    const slippageBN = BigInt(
      BigNumber(slippage)
        .multipliedBy(100)
        .dp(0, BigNumber.ROUND_FLOOR)
        .toFixed()
    )
    const priorityFee = await this.calcPriorityFee(
      this.tradeSetting,
      walletPublicKey.toBase58()
    )
    const respTx = await betSdk.getSellBySolAmountTransaction(
      walletPublicKey,
      mintPublicKey,
      solAmountBN,
      slippageBN
    )
    const tradeTx = await packageTransaction(
      tradeProvider,
      respTx.instructions,
      undefined,
      {
        priorityFee: priorityFee,
        tableAccounts: [ENV_PARAMS.PLATFORM_LOOKUP_TABLE]
      }
    )

    return {
      tx: tradeTx.transaction,
      latestBlockhash: tradeTx.latestBlockhash
    }
  }

  private async calcAmount(params: IComputeProps): Promise<any> {
    let tokenMint
    if (params.inputMint === NATIVE_MINT.toBase58()) {
      tokenMint = params.outputMint
    } else {
      tokenMint = params.inputMint
    }
    const mintPublicKey = new PublicKey(tokenMint)
    const amountBigint = BigInt(params.amount)
    const swapBase = params.base
    const slippageBps = params.slippageBps || 0
    // const betState = await this.betSdk.getBetStateAccount(mintPublicKey, DEFAULT_COMMITMENT);
    // if (!betState) {
    //   throw new Error(`Bet bet account not found: ${tokenMint}`);
    // }
    // const curveAccount = await this.betSdk.getCurveStateAccount(mintPublicKey, DEFAULT_COMMITMENT);
    // if (!curveAccount) {
    //   throw new Error(`Curve state account not found: ${tokenMint}`);
    // }
    const result = {
      amount: '',
      thresholdAmount: ''
      // feeRate: Number(globalAccount.feeBasisPoints),
      // feeAmount: feeAmountBn.lt(1) ? '1' : feeAmountBn.dp(0, BigNumber.ROUND_CEIL).toFixed(),
      // feeMint: NATIVE_MINT.toBase58(),
    }
    const betSdk = this.getBetSdk()
    if (params.inputMint === NATIVE_MINT.toBase58()) {
      // 买入token
      if (swapBase === EnumSwapBase.IN) {
        // 固定SOL数量
        const tokenAmount = await betSdk.getTokenAmountOut(
          mintPublicKey,
          amountBigint
        )
        const minReciveToken = BigNumber(100)
          .minus(slippageBps)
          .multipliedBy(String(tokenAmount))
          .dp(0, BigNumber.ROUND_FLOOR)
          .toFixed()
        result.amount = String(tokenAmount)
        result.thresholdAmount = minReciveToken
      } else if (swapBase === EnumSwapBase.OUT) {
        // 固定toeken数量
        const solAmount = await betSdk.getMinSolAmountIn(
          mintPublicKey,
          amountBigint
        )
        const maxCostSol = BigNumber(100)
          .plus(slippageBps)
          .multipliedBy(String(solAmount))
          .dp(0, BigNumber.ROUND_CEIL)
          .toFixed()
        result.amount = String(solAmount)
        result.thresholdAmount = maxCostSol
      }
    } else {
      // 卖出token
      if (swapBase === EnumSwapBase.IN) {
        const solAmount = await betSdk.getSolAmountOut(
          mintPublicKey,
          amountBigint
        )
        const minReciveSol = BigNumber(100)
          .minus(slippageBps)
          .multipliedBy(String(solAmount))
          .dp(0, BigNumber.ROUND_FLOOR)
          .toFixed()
        result.amount = String(solAmount)
        result.thresholdAmount = minReciveSol
      } else if (swapBase === EnumSwapBase.OUT) {
        const tokenAmount = await betSdk.getMinTokenAmountIn(
          mintPublicKey,
          amountBigint
        )
        const maxCostToken = BigNumber(100)
          .plus(slippageBps)
          .multipliedBy(String(tokenAmount))
          .dp(0, BigNumber.ROUND_CEIL)
          .toFixed()
        result.amount = String(tokenAmount)
        result.thresholdAmount = maxCostToken
      }
    }
    return result
  }

  /**
   * 计算兑换数量数据
   * @param params
   * @returns
   */
  private async calcComputeData(params: IComputeProps): Promise<any> {
    const respCalc = await this.calcAmount(params)
    // const priceEffect = await this.getPriceEffect(params.amount, direction, usePcMint);
    let inAmount, outAmount
    if (params.base === EnumSwapBase.IN) {
      inAmount = params.amount
      outAmount = respCalc.amount
    } else if (params.base === EnumSwapBase.OUT) {
      inAmount = respCalc.amount
      outAmount = params.amount
    }
    const computeData = {
      swapType: params.base,
      inputMint: params.inputMint,
      inputAmount: inAmount,
      outputMint: params.outputMint,
      outputAmount: outAmount,
      otherAmountThreshold: respCalc.thresholdAmount,
      slippageBps: params.slippageBps,
      // "priceImpactPct":  new BigNumber(priceEffect || 0).multipliedBy(100).toFixed(2),
      routePlan: [
        {
          inputMint: params.inputMint,
          outputMint: params.outputMint,
          feeMint: respCalc.feeMint,
          feeRate: respCalc.feeRate,
          feeAmount: respCalc.feeAmount
          // "poolId": "3EJXQNLncppDUQo9j7ysbEbwpCRKbDb2H34ytc8nuS7m",
          // "remainingAccounts": []
        }
      ]
    }
    const result = {
      data: {
        success: true,
        data: computeData
      }
    }
    return result
  }

  // 获取质押池子
  public async getStakePoolStates() {
    const stakePoolStates = await this.stakeVoteSdk.getStakePoolStates()
    return stakePoolStates.map<StakePool>((stakePool) => {
      const stakePeriod = stakePool.account.stakePeriod.toNumber()
      const stakePeriodByMinutes = dayjs
        .duration(stakePeriod, 'seconds')
        .asMinutes()
      const stakePeriodByHours = dayjs
        .duration(stakePeriod, 'seconds')
        .asHours()
      const stakePeriodByDays = dayjs.duration(stakePeriod, 'seconds').asDays()
      const stakePeriodByMonths = stakePeriodByDays / 30

      return {
        publicKey: stakePool.publicKey,
        decimal: stakePool.account.decimal,
        rewardGrowBy: Number(
          min2Max(
            stakePool.account.rewardGrowBy.toNumber(),
            stakePool.account.decimal
          )
        ),
        rewardGrowMinBy: Number(
          min2Max(
            stakePool.account.rewardGrowMinBy.toNumber(),
            stakePool.account.decimal
          )
        ),
        stakePeriod,
        stakePeriodByMinutes,
        stakePeriodByHours,
        stakePeriodByDays,
        stakePeriodByMonths
      }
    })
  }

  public async getUserStakedPool(stakePool: StakePool) {
    const tradeProvider = this.getTradeProvider()
    const walletPublicKey = tradeProvider.publicKey!
    const resp = await this.stakeVoteSdk.getUserStakeVoteStateAccount(
      stakePool.publicKey,
      walletPublicKey
    )
    const activeBallotBoxStates =
      await this.stakeVoteSdk.getActiveBallotBoxStateAccount()
    const userActiveBallotBoxStates =
      await this.stakeVoteSdk.getUserActiveBallotBoxStateAccount(
        stakePool.publicKey,
        walletPublicKey
      )
    const lockAmount = await this.stakeVoteSdk.lockedAmount(
      walletPublicKey,
      stakePool.publicKey
    )
    const vouchers = await this.stakeVoteSdk.getUserVouchersByPool(
      stakePool.publicKey,
      walletPublicKey
    )

    userActiveBallotBoxStates?.ballotLocks.map((ballotLock) => {
      console.log('>>>>>> userActiveBallotBoxState: ', {
        stakePool,
        'stakePool.publicKey': stakePool.publicKey.toBase58(),
        'ballotLock.ballotBot': ballotLock.ballotBot.toBase58(),
        'ballotLock.stakeLock': ballotLock.stakeLock.toString(),
        'activeBallotBoxStates?.ballotBots':
          activeBallotBoxStates?.ballotBots.map((ballotBot) =>
            ballotBot.toBase58()
          )
      })
    })

    if (!resp) return null

    const currentTime = dayjs().unix()
    let stakeReleaseNotRecived: string, stakeNotRelease: string
    if (currentTime > Number(resp.timeEndAt)) {
      // 质押已过期，
      stakeReleaseNotRecived = new BigNumber(Number(resp.stakeAllTotal))
        .minus(Number(resp.stakeReleaseRecived))
        .minus(Number(lockAmount))
        .dividedBy(Math.pow(10, Decimal.BET))
        .toFixed(Decimal.BET)

      stakeNotRelease = '0'
    } else {
      // 已释放未领取的质押 =（当前时间 - 上次释放时间）* 释放速率 + 已释放未领取
      stakeReleaseNotRecived = new BigNumber(currentTime)
        .minus(Number(resp.timeStakeRelease))
        .multipliedBy(Number(resp.stakeReleasePerTime))
        .dividedBy(Math.pow(10, Number(resp.decimal)))
        .plus(Number(resp.stakeReleaseNotRecived))
        .minus(Number(lockAmount))
        .dividedBy(Math.pow(10, Decimal.BET))
        .toFixed(Decimal.BET)

      // 未释放的质押数量 =（结束时间 - 当前时间）* 质押释放速度
      stakeNotRelease = new BigNumber(Number(resp.timeEndAt))
        .minus(currentTime)
        .multipliedBy(Number(resp.stakeReleasePerTime))
        .dividedBy(Math.pow(10, Number(resp.decimal)))
        .dividedBy(Math.pow(10, Decimal.BET))
        .toFixed(Decimal.BET)
    }

    // if (stakePool.stakePeriod === 60 * 60 * 24 * 30)
    //   console.log('>>>>>> getUserStakedPool: ', {
    //     stakeNotRelease,
    //     stakePool,
    //     vouchers,
    //     resp
    //   })

    return {
      publicKey: resp.stakePoolState,
      timeEndAt: Number(resp.timeEndAt),
      timeStartAt: Number(resp.timeStartAt),
      periodTotal: Number(resp.periodTotal),
      stakeReleaseNotRecived:
        Number(stakeReleaseNotRecived) < 0 ? '0' : stakeReleaseNotRecived,
      stakeNotRelease,
      stakeTotal: new BigNumber(Number(resp.stakeTotal))
        .dividedBy(Math.pow(10, Decimal.BET))
        .toNumber(),
      voteUsed: new BigNumber(Number(resp.voteUsed))
        .dividedBy(Math.pow(10, Decimal.veToken))
        .toNumber(),
      voteTotal: new BigNumber(Number(resp.voteTotal))
        .dividedBy(Math.pow(10, Decimal.veToken))
        .toNumber(),
      voteAvailable: new BigNumber(Number(vouchers.voteLeft))
        .dividedBy(Math.pow(10, Decimal.veToken))
        .toNumber(),
      voteLocked: new BigNumber(Number(lockAmount))
        .dividedBy(Math.pow(10, Decimal.veToken))
        .toNumber()
    } satisfies UserStakedPool
  }

  public async stake(
    stakePoolState: PublicKey,
    amount: BN,
    isStaked?: boolean,
    type?: StakeAddType
  ): Promise<ITradeTransaction> {
    const tradeProvider = this.getTradeProvider()
    const walletPublicKey = tradeProvider.publicKey!

    const parmas: Parameters<typeof this.stakeVoteSdk.getStakeTransactions> = [
      walletPublicKey,
      stakePoolState,
      amount
    ]

    let stakeTx: Transaction
    if (isStaked && type !== undefined) {
      // append Stake
      stakeTx = await this.stakeVoteSdk.getStakeAddTransactions(...parmas, type)
    } else {
      // create Stake
      stakeTx = await this.stakeVoteSdk.getStakeTransactions(...parmas)
    }

    const priorityFee = await this.calcPriorityFee(
      this.tradeSetting,
      walletPublicKey.toBase58()
    )

    const tradeTx = await packageTransaction(
      tradeProvider,
      stakeTx.instructions,
      undefined,
      {
        priorityFee: priorityFee,
        tableAccounts: [ENV_PARAMS.PLATFORM_LOOKUP_TABLE],
        isModifyComputeUnits: true
      }
    )
    return {
      tx: tradeTx.transaction,
      latestBlockhash: tradeTx.latestBlockhash
    }
  }

  public async stakeWithdraw(
    stakePoolState: PublicKey,
    amount: BN
  ): Promise<ITradeTransaction> {
    const tradeProvider = this.getTradeProvider()
    const walletPublicKey = tradeProvider.publicKey!
    const parmas: Parameters<typeof this.stakeVoteSdk.getStakeTransactions> = [
      walletPublicKey,
      stakePoolState,
      amount
    ]
    const withdrawTx = await this.stakeVoteSdk.getStakeWithdrawTransactions(
      ...parmas
    )
    const priorityFee = await this.calcPriorityFee(
      this.tradeSetting,
      walletPublicKey.toBase58()
    )
    // const tx = await this.packageTransaction(
    //   withdrawTx.instructions,
    //   priorityFee
    // )
    // return await this.sendAndConfirm(tx)
    const tradeTx = await packageTransaction(
      tradeProvider,
      withdrawTx.instructions,
      undefined,
      {
        priorityFee: priorityFee,
        tableAccounts: [ENV_PARAMS.PLATFORM_LOOKUP_TABLE],
        isModifyComputeUnits: true
      }
    )
    return {
      tx: tradeTx.transaction,
      latestBlockhash: tradeTx.latestBlockhash
    }
  }

  public async getUserVouchers() {
    const tradeProvider = this.getTradeProvider()
    const walletPublicKey = tradeProvider.publicKey!
    const { userVouchers, decimal } =
      await this.stakeVoteSdk.getUserVouchers(walletPublicKey)

    return {
      amount: Number(userVouchers),
      decimal
    }
  }

  public async vote(
    ballotBoxState: PublicKey,
    amount: bigint
  ): Promise<ITradeTransaction> {
    const tradeProvider = this.getTradeProvider()
    const walletPublicKey = tradeProvider.publicKey!
    const voteTx = await this.stakeVoteSdk.getAutoVoteTransactions(
      walletPublicKey,
      ballotBoxState,
      amount
    )
    const priorityFee = await this.calcPriorityFee(
      this.tradeSetting,
      walletPublicKey.toBase58()
    )
    // const tx = await this.packageTransaction(voteTx.instructions, priorityFee)
    // return await this.sendAndConfirm(tx)
    const tradeTx = await packageTransaction(
      tradeProvider,
      voteTx.instructions,
      undefined,
      {
        priorityFee: priorityFee,
        tableAccounts: [ENV_PARAMS.PLATFORM_LOOKUP_TABLE],
        isModifyComputeUnits: true
      }
    )
    return {
      tx: tradeTx.transaction,
      latestBlockhash: tradeTx.latestBlockhash
    }
  }

  public async getVoteReward(boxId: string) {
    const tradeProvider = this.getTradeProvider()
    const voteRewardStateAccount =
      await this.stakeVoteSdk.getVoteRewardStateAccount(new PublicKey(boxId))
    if (
      voteRewardStateAccount &&
      voteRewardStateAccount.isStart &&
      voteRewardStateAccount.amount
    ) {
      const respReward = await this.stakeVoteSdk.getUserRewards(
        new PublicKey(boxId),
        tradeProvider.publicKey
      )
      const mint = respReward.rewardMint
      const metadata = await getTokenMetadata(mint.toBase58()).catch(
        () => undefined
      )
      const mintInfo = await getMint(tradeProvider.connection, mint).catch(
        () => undefined
      )
      // console.log('metadata', metadata, respReward)
      return {
        userRewards: respReward.userRewards,
        rewardMint: respReward.rewardMint,
        rewardDecimals: mintInfo?.decimals || 0,
        rewardSymbol: metadata?.symbol || ''
      }
    }
    return undefined
  }

  public async getClaimVoteRewardTx(
    boxIds: string[]
  ): Promise<ITradeTransaction> {
    if (!boxIds.length) {
      return Promise.reject()
    }
    const tradeProvider = this.getTradeProvider()
    const boxPublickeys = boxIds.map((id) => new PublicKey(id))
    const respTx = await this.stakeVoteSdk.getBatchRewardReceiveTransactions(
      tradeProvider.publicKey,
      boxPublickeys
    )
    const tradeTx = await packageTransaction(
      tradeProvider,
      respTx.instructions,
      undefined
    )

    return {
      tx: tradeTx.transaction,
      latestBlockhash: tradeTx.latestBlockhash
    }
  }
}

// export const fetchWithTimeout = async (url: string, options: any = {}, timeout: number = 60000) => {
//   const controller = new AbortController();
//   const timeoutId = setTimeout(() => controller.abort(), timeout);
//   const response = await fetch(url, {
//     ...options,
//     signal: controller.signal,
//   });
//   clearTimeout(timeoutId);
//   return response;
// }

// export async function fetchQuiknodePriorityFees(
//   endpoint: string,
//   account?: string,
//   lastBlocks?: number,
//   apiVersion?: string
// ): Promise<any> {
//   // Only include params that are defined
//   const params = {
//     'account': account,
//     'last_n_blocks': lastBlocks,
//     'api_version': apiVersion
//   }
//   const payload = {
//     method: 'qn_estimatePriorityFees',
//     params: JSON.parse(JSON.stringify(params)),
//     id: 1,
//     jsonrpc: '2.0',
//   };
//   const data = await fetchWithTimeout(endpoint, {
//     method: 'POST',
//     headers: {
//       'Content-Type': 'application/json',
//     },
//     body: JSON.stringify(payload),
//   }, 10000).then((resp) => {
//     if (resp.ok) {
//       return resp.json();
//     } else {
//       throw new Error(`HTTP error! status: ${resp.status}`);
//     }
//   });
//   return data.result;
// }

// export async function fetchHeliusPriorityFees(
//   endpoint: string,
//   account?: string
// ): Promise<any> {
//   const payload = {
//     'jsonrpc': "2.0",
//     'id': "1",
//     'method': "getPriorityFeeEstimate",
//     'params': [{
//       "accountKeys": account ? [account] : [],
//       "options": {
//         "includeAllPriorityFeeLevels": true
//       }
//     }]
//   }

//   const data = await fetchWithTimeout(endpoint, {
//     method: 'POST',
//     headers: {
//       'Content-Type': 'application/json',
//     },
//     body: JSON.stringify(payload),
//   }, 10000).then((resp) => {
//     if (resp.ok) {
//       return resp.json();
//     } else {
//       throw new Error(`HTTP error! status: ${resp.status}`);
//     }
//   });
//   return data.result;
// }

// export const getRaydiumPriorityFee = async (): Promise<any> => {
//   // <{
//   //   id: string
//   //   success: boolean
//   //   data: { default: { vh: number; h: number; m: number } }
//   //   msg?: string,
//   // }>
//   const resp: any = fetch(`https://transaction-v1.raydium.io/main/auto-fee`, {
//     method: 'GET',
//   }).then((resp) => resp.json())
//   if (resp?.data?.success) {
//     return resp.data;
//   } else {
//     throw new Error(`Error: ${resp.data.msg}`);
//   }

// }
